import type { GroundControlConfigApi } from 'api';
import { translate } from 'i18n';
import type { JSX } from 'react';
import {
  AccountBalance,
  AddBox,
  Addchart,
  Article,
  AttachMoney,
  Badge,
  BarChart,
  Business,
  Calculate,
  CameraAlt,
  CameraEnhance,
  DirectionsBoat,
  DocumentScanner,
  FindInPage,
  Help,
  HotTub,
  Inventory,
  LocalShipping,
  ManageSearchRounded,
  MenuBook,
  MonetizationOn,
  PeopleOutlined,
  Person,
  PersonAddAlt,
  PersonSearch,
  PhotoCameraBackOutlined,
  PointOfSale,
  PostAdd,
  RequestQuote,
  Settings,
  ShoppingCartCheckout,
  StackedLineChart,
  SyncOutlined,
  TrendingUp,
} from 'ui/icons';

const JIRA_SUPPORT_PORTAL_URL = 'https://safi.atlassian.net/servicedesk/customer/portals';
export interface TabLink {
  description: string;
  href: string;
  icon: JSX.Element;
  /**
   * Whether the link is internal or external.
   * Internal links use the `Link` component from `react-router-dom` for improved performance.
   * External links use the `a` tag.
   */
  isLegacy: boolean;
  label: string;
}

export interface Tab {
  icon: JSX.Element;
  label: string;
  links: TabLink[];
}

export const getTabs = (settings?: GroundControlConfigApi): Tab[] => [
  {
    icon: <TrendingUp />,
    label: translate('backlog.groundControl.tabs.trade.label', { namespace: 'features' }),
    links: [
      {
        description: translate('backlog.groundControl.tabs.trade.links.createDemandSignal.description', {
          namespace: 'features',
        }),
        href: '/ground-control/trade-signals/demand/create/',
        icon: <Addchart />,
        isLegacy: false,
        label: translate('backlog.groundControl.tabs.trade.links.createDemandSignal.label', { namespace: 'features' }),
      },
      {
        description: translate('backlog.groundControl.tabs.trade.links.createSupplySignal.description', {
          namespace: 'features',
        }),
        href: '/ground-control/trade-signals/supply/create/',
        icon: <StackedLineChart />,
        isLegacy: false,
        label: translate('backlog.groundControl.tabs.trade.links.createSupplySignal.label', { namespace: 'features' }),
      },
      {
        description: translate('backlog.groundControl.common.descriptions.createPurchaseOrderAndSendToSeller', {
          namespace: 'features',
        }),
        href: '/ground-control/create-purchase-order/',
        icon: <ShoppingCartCheckout />,
        isLegacy: false,
        label: translate('backlog.groundControl.common.labels.createPurchaseOrder', { namespace: 'features' }),
      },
      {
        description: translate('backlog.groundControl.common.descriptions.createSalesOrderAndSendToBuyer', {
          namespace: 'features',
        }),
        href: '/ground-control/v2/create-sales-order/',
        icon: <AttachMoney />,
        isLegacy: false,
        label: translate('backlog.groundControl.common.labels.createSalesOrder', { namespace: 'features' }),
      },
      {
        description: translate('backlog.groundControl.common.descriptions.viewEditAndMatch', {
          namespace: 'features',
        }),
        href: '/ground-control/purchase_orders/?match_status=0+UN&match_status=4+PA',
        icon: <Inventory />,
        isLegacy: true,
        label: translate('backlog.groundControl.common.labels.viewPurchaseOrders', { namespace: 'features' }),
      },
      {
        description: translate('backlog.groundControl.common.descriptions.viewEditAndMatch', {
          namespace: 'features',
        }),
        href: '/ground-control/sales_orders/?match_status=0+UN&match_status=4+PA',
        icon: <PostAdd />,
        isLegacy: true,
        label: translate('backlog.groundControl.common.labels.viewSalesOrders', { namespace: 'features' }),
      },
      {
        description: translate('backlog.groundControl.common.descriptions.calculateDealProfitability', {
          namespace: 'features',
        }),
        href: '/ground-control/profitability-calculator/',
        icon: <Calculate />,
        isLegacy: false,
        label: translate('backlog.groundControl.common.labels.dealProfitabilityCalculator', {
          namespace: 'features',
        }),
      },
      {
        description: translate('backlog.groundControl.tabs.trade.links.transportMetabase.description', {
          namespace: 'features',
        }),
        href: 'https://metabase.internal.safi.co/dashboard/39-transport-quote?am=&carrier=&delivery_country=&delivery_location=&equipment_type=&expired_on_date=&incoterm=&load_country=&load_location=&pod=&pol=&quoted_on_date=&transport_provider=&type_of_goods=',
        icon: <DirectionsBoat />,
        isLegacy: true,
        label: translate('backlog.groundControl.tabs.trade.links.transportMetabase.label', {
          namespace: 'features',
        }),
      },
      {
        description: translate('backlog.groundControl.tabs.trade.links.qualityReport.description', {
          namespace: 'features',
        }),
        href: '/quality/',
        icon: <CameraEnhance />,
        isLegacy: false,
        label: translate('backlog.groundControl.tabs.trade.links.qualityReport.label', { namespace: 'features' }),
      },
      {
        description: translate('backlog.groundControl.tabs.trade.links.generateDocuments.description', {
          namespace: 'features',
        }),
        href: '/ground-control/document-generation/',
        icon: <DocumentScanner />,
        isLegacy: false,
        label: translate('backlog.groundControl.tabs.trade.links.generateDocuments.label', {
          namespace: 'features',
        }),
      },
    ],
  },
  {
    icon: <Person />,
    label: translate('backlog.groundControl.tabs.usersAndCompanies.label', { namespace: 'features' }),
    links: [
      {
        description: translate('backlog.groundControl.tabs.usersAndCompanies.links.createUserAndCompany.description', {
          namespace: 'features',
        }),
        href: '/ground-control/user_onboarding/',
        icon: <PersonAddAlt />,
        isLegacy: true,
        label: translate('backlog.groundControl.tabs.usersAndCompanies.links.createUserAndCompany.label', {
          namespace: 'features',
        }),
      },
      {
        description: translate('backlog.groundControl.tabs.usersAndCompanies.links.hijackUser.description', {
          namespace: 'features',
        }),
        href: '/ground-control/admin/accounts/customuser/',
        icon: <HotTub />,
        isLegacy: true,
        label: translate('backlog.groundControl.tabs.usersAndCompanies.links.hijackUser.label', {
          namespace: 'features',
        }),
      },
      {
        description: translate('backlog.groundControl.tabs.usersAndCompanies.links.viewAllUsers.description', {
          namespace: 'features',
        }),
        href:
          settings?.hubspot_contacts_view_url ||
          'https://app-eu1.hubspot.com/contacts/25870036/objects/0-1/views/21699080/list',
        icon: <PeopleOutlined />,
        isLegacy: true,
        label: translate('backlog.groundControl.tabs.usersAndCompanies.links.viewAllUsers.label', {
          namespace: 'features',
        }),
      },
      {
        description: translate('backlog.groundControl.tabs.usersAndCompanies.links.viewTradeCompanies.description', {
          namespace: 'features',
        }),
        href: '/ground-control/admin/trade_companies/tradecompany/',
        icon: <Business />,
        isLegacy: true,
        label: translate('backlog.groundControl.tabs.usersAndCompanies.links.viewTradeCompanies.label', {
          namespace: 'features',
        }),
      },
      {
        description: translate('backlog.groundControl.tabs.usersAndCompanies.links.staffOnboarding.description', {
          namespace: 'features',
        }),
        href: '/ground-control/admin_onboarding/',
        icon: <Badge />,
        isLegacy: true,
        label: translate('backlog.groundControl.tabs.usersAndCompanies.links.staffOnboarding.label', {
          namespace: 'features',
        }),
      },
      {
        description: translate('backlog.groundControl.tabs.usersAndCompanies.links.copySessions.description', {
          namespace: 'features',
        }),
        href: '/ground-control/copy-sessions/',
        icon: <ManageSearchRounded />,
        isLegacy: false,
        label: translate('backlog.groundControl.tabs.usersAndCompanies.links.copySessions.label', {
          namespace: 'features',
        }),
      },
    ],
  },
  {
    icon: <Article />,
    label: translate('backlog.groundControl.tabs.contracts.label', { namespace: 'features' }),
    links: [
      {
        description: translate('backlog.groundControl.common.descriptions.createPurchaseOrderAndSendToSeller', {
          namespace: 'features',
        }),
        href: '/ground-control/create-purchase-order/',
        icon: <ShoppingCartCheckout />,
        isLegacy: false,
        label: translate('backlog.groundControl.common.labels.createPurchaseOrder', { namespace: 'features' }),
      },
      {
        description: translate('backlog.groundControl.common.descriptions.viewEditAndMatch', {
          namespace: 'features',
        }),
        href: '/ground-control/purchase_orders/?match_status=0+UN&match_status=4+PA',
        icon: <Inventory />,
        isLegacy: true,
        label: translate('backlog.groundControl.common.labels.viewPurchaseOrders', { namespace: 'features' }),
      },
      {
        description: translate('backlog.groundControl.common.descriptions.createSalesOrderAndSendToBuyer', {
          namespace: 'features',
        }),
        href: '/ground-control/v2/create-sales-order/',
        icon: <AttachMoney />,
        isLegacy: false,
        label: translate('backlog.groundControl.common.labels.createSalesOrder', { namespace: 'features' }),
      },
      {
        description: translate('backlog.groundControl.common.descriptions.viewEditAndMatch', {
          namespace: 'features',
        }),
        href: '/ground-control/sales_orders/?match_status=0+UN&match_status=4+PA',
        icon: <PostAdd />,
        isLegacy: true,
        label: translate('backlog.groundControl.common.labels.viewSalesOrders', { namespace: 'features' }),
      },
    ],
  },
  {
    icon: <PointOfSale />,
    label: translate('backlog.groundControl.tabs.deals.label', { namespace: 'features' }),
    links: [
      {
        description: translate('backlog.groundControl.tabs.deals.links.goToDealHub.description', {
          namespace: 'features',
        }),
        href: '/post-deal/dealhub/',
        icon: <Settings />,
        isLegacy: true,
        label: translate('backlog.groundControl.tabs.deals.links.goToDealHub.label', { namespace: 'features' }),
      },
      {
        description: translate('backlog.groundControl.tabs.deals.links.viewAllDeals.description', {
          namespace: 'features',
        }),
        href: '/post-deal/dealhub/deal/list/',
        icon: <RequestQuote />,
        isLegacy: true,
        label: translate('backlog.groundControl.tabs.deals.links.viewAllDeals.label', { namespace: 'features' }),
      },
    ],
  },
  {
    icon: <MonetizationOn />,
    label: translate('backlog.groundControl.tabs.finance.label', { namespace: 'features' }),
    links: [
      {
        description: translate('backlog.groundControl.tabs.finance.links.manageCosts.description', {
          namespace: 'features',
        }),
        href: '/ground-control/admin/finance/cost/',
        icon: <MonetizationOn />,
        isLegacy: true,
        label: translate('backlog.groundControl.tabs.finance.links.manageCosts.label', { namespace: 'features' }),
      },
      {
        description: translate('backlog.groundControl.tabs.finance.links.manageRevenue.description', {
          namespace: 'features',
        }),
        href: '/ground-control/admin/finance/revenue/',
        icon: <MonetizationOn />,
        isLegacy: true,
        label: translate('backlog.groundControl.tabs.finance.links.manageRevenue.label', { namespace: 'features' }),
      },
      {
        description: translate('backlog.groundControl.tabs.finance.links.createShippingRate.description', {
          namespace: 'features',
        }),
        href: '/ground-control/create_shipping_rate/',
        icon: <DirectionsBoat />,
        isLegacy: true,
        label: translate('backlog.groundControl.tabs.finance.links.createShippingRate.label', {
          namespace: 'features',
        }),
      },
      {
        description: translate('backlog.groundControl.tabs.finance.links.manageBanks.description', {
          namespace: 'features',
        }),
        href: '/ground-control/admin/accounts/bankdetails/',
        icon: <AccountBalance />,
        isLegacy: true,
        label: translate('backlog.groundControl.tabs.finance.links.manageBanks.label', { namespace: 'features' }),
      },
      {
        description: translate('backlog.groundControl.tabs.finance.links.manageExternalCompanies.description', {
          namespace: 'features',
        }),
        href: '/ground-control/admin/finance/externalcompanydetails/',
        icon: <MonetizationOn />,
        isLegacy: true,
        label: translate('backlog.groundControl.tabs.finance.links.manageExternalCompanies.label', {
          namespace: 'features',
        }),
      },
      {
        description: translate('backlog.groundControl.common.descriptions.calculateDealProfitability', {
          namespace: 'features',
        }),
        href: '/ground-control/profitability-calculator/',
        icon: <Calculate />,
        isLegacy: false,
        label: translate('backlog.groundControl.common.labels.dealProfitabilityCalculator', {
          namespace: 'features',
        }),
      },
      {
        description: translate('backlog.groundControl.tabs.finance.links.marketPernPrices.description', {
          namespace: 'features',
        }),
        href: '/ground-control/admin/common/pernprice/',
        icon: <BarChart />,
        isLegacy: true,
        label: translate('backlog.groundControl.tabs.finance.links.marketPernPrices.label', {
          namespace: 'features',
        }),
      },
      {
        description: translate('backlog.groundControl.tabs.finance.links.manageFinancing.description', {
          namespace: 'features',
        }),
        href: '/ground-control/financing-home/',
        icon: <ShoppingCartCheckout />,
        isLegacy: false,
        label: translate('backlog.groundControl.tabs.finance.links.manageFinancing.label', {
          namespace: 'features',
        }),
      },
    ],
  },
  {
    icon: <LocalShipping />,
    label: translate('backlog.groundControl.tabs.transport.label', { namespace: 'features' }),
    links: [
      {
        description: translate('backlog.groundControl.tabs.transport.links.manageCarriers.description', {
          namespace: 'features',
        }),
        href: '/ground-control/admin/tracker/carrier/',
        icon: <MonetizationOn />,
        isLegacy: true,
        label: translate('backlog.groundControl.tabs.transport.links.manageCarriers.label', {
          namespace: 'features',
        }),
      },
      {
        description: translate('backlog.groundControl.tabs.transport.links.viewPorts.description', {
          namespace: 'features',
        }),
        href: '/ground-control/admin/accounts/port/',
        icon: <DirectionsBoat />,
        isLegacy: true,
        label: translate('backlog.groundControl.tabs.transport.links.viewPorts.label', { namespace: 'features' }),
      },
      {
        description: translate('backlog.groundControl.tabs.transport.links.syncTransportCosts.description', {
          namespace: 'features',
        }),
        href: '/ground-control/sync-transport-costs/',
        icon: <SyncOutlined />,
        isLegacy: true,
        label: translate('backlog.groundControl.tabs.transport.links.syncTransportCosts.label', {
          namespace: 'features',
        }),
      },
    ],
  },
  {
    icon: <FindInPage />,
    label: translate('backlog.groundControl.tabs.inspections.label', { namespace: 'features' }),
    links: [
      {
        description: translate('backlog.groundControl.tabs.inspections.links.inspections.description', {
          namespace: 'features',
        }),
        href: '/ground-control/inspections/',
        icon: <FindInPage />,
        isLegacy: false,
        label: translate('backlog.groundControl.tabs.inspections.links.inspections.label', {
          namespace: 'features',
        }),
      },
      {
        description: translate('backlog.groundControl.tabs.inspections.links.createInspection.description', {
          namespace: 'features',
        }),
        href: '/ground-control/inspections/create/',
        icon: <AddBox />,
        isLegacy: false,
        label: translate('backlog.groundControl.tabs.inspections.links.createInspection.label', {
          namespace: 'features',
        }),
      },
      {
        description: translate('backlog.groundControl.tabs.inspections.links.viewUpcomingLoads.description', {
          namespace: 'features',
        }),
        href: '/ground-control/inspections/loads/',
        icon: <LocalShipping />,
        isLegacy: false,
        label: translate('backlog.groundControl.tabs.inspections.links.viewUpcomingLoads.label', {
          namespace: 'features',
        }),
      },
      {
        description: translate('backlog.groundControl.tabs.inspections.links.inspectionCompanies.description', {
          namespace: 'features',
        }),
        href: '/ground-control/inspections/companies/',
        icon: <Business />,
        isLegacy: false,
        label: translate('backlog.groundControl.tabs.inspections.links.inspectionCompanies.label', {
          namespace: 'features',
        }),
      },
      {
        description: translate('backlog.groundControl.tabs.inspections.links.inspectors.description', {
          namespace: 'features',
        }),
        href: '/ground-control/inspections/inspectors/',
        icon: <PersonSearch />,
        isLegacy: false,
        label: translate('backlog.groundControl.tabs.inspections.links.inspectors.label', {
          namespace: 'features',
        }),
      },
      {
        description: translate('backlog.groundControl.tabs.inspections.links.addSiteEvidence.description', {
          namespace: 'features',
        }),
        href: '/quality/',
        icon: <CameraAlt />,
        isLegacy: false,
        label: translate('backlog.groundControl.tabs.inspections.links.addSiteEvidence.label', {
          namespace: 'features',
        }),
      },
      {
        description: translate('backlog.groundControl.tabs.inspections.links.addExternalEvidence.description', {
          namespace: 'features',
        }),
        href: '/quality/external-evidence/',
        icon: <PhotoCameraBackOutlined />,
        isLegacy: false,
        label: translate('backlog.groundControl.tabs.inspections.links.addExternalEvidence.label', {
          namespace: 'features',
        }),
      },
    ],
  },
  {
    icon: <Settings />,
    label: translate('backlog.groundControl.tabs.advanced.label', { namespace: 'features' }),
    links: [
      {
        description: translate('backlog.groundControl.tabs.advanced.links.pernType.description', {
          namespace: 'features',
        }),
        href: '/ground-control/admin/materials/perntype/',
        icon: <MonetizationOn />,
        isLegacy: true,
        label: translate('backlog.groundControl.tabs.advanced.links.pernType.label', { namespace: 'features' }),
      },
      {
        description: translate('backlog.groundControl.tabs.advanced.links.manageMaterialProducts.description', {
          namespace: 'features',
        }),
        href: '/ground-control/material-products/',
        icon: <ManageSearchRounded />,
        isLegacy: false,
        label: translate('backlog.groundControl.tabs.advanced.links.manageMaterialProducts.label', {
          namespace: 'features',
        }),
      },
    ],
  },
  {
    icon: <Help />,
    label: translate('backlog.groundControl.tabs.support.label', { namespace: 'features' }),
    links: [
      {
        description: translate('backlog.groundControl.tabs.support.links.helpAndLearn.description', {
          namespace: 'features',
        }),
        href: '/help',
        icon: <MenuBook />,
        isLegacy: false,
        label: translate('backlog.groundControl.tabs.support.links.helpAndLearn.label', { namespace: 'features' }),
      },
      {
        description: translate('backlog.groundControl.tabs.support.links.getSupport.description', {
          namespace: 'features',
        }),
        href: JIRA_SUPPORT_PORTAL_URL,
        icon: <Help />,
        isLegacy: true,
        label: translate('backlog.groundControl.tabs.support.links.getSupport.label', { namespace: 'features' }),
      },
    ],
  },
];
